import React, { useContext, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import UserContext from '../../../context/UserContext/UserContext';
import ErrorAlert from '../../../components/ErrorAlert/ErrorAlert';
import BotBuilderService from '../../../services/BotBuilderService/BotBuilderService';
import Intent from '../../../types/BotBuilder/Intent';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';

interface DeleteIntentModalProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  intent: Intent;
}
function DeleteIntentModal({
  intent,
  modalOpen,
  setModalOpen
}: Readonly<DeleteIntentModalProps>): React.ReactElement {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user } = useContext(UserContext);
  const { currentChatbot } = React.useContext(ChatbotContext);

  const navigate = useNavigate();
  const location = useLocation();

  const handleClose = (): void => setModalOpen(false);
  const deleteIntent = (): void => {
    setIsDeleting(true);
    setErrorMessage('');
    if (user && intent.id && currentChatbot) {
      BotBuilderService.deleteIntent(currentChatbot.id, intent.id)
        .then(() => {
          const returnState = location.state;
          if (
            returnState?.returnQuery !== undefined ||
            returnState?.returnPage !== undefined ||
            returnState?.returnResultsPerPage !== undefined
          ) {
            navigate('/training', {
              state: {
                returnQuery: returnState.returnQuery,
                returnPage: returnState.returnPage,
                returnResultsPerPage: returnState.returnResultsPerPage
              }
            });
          } else {
            navigate('/training');
          }
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          const error =
            err.apiError?.message ||
            'Something went wrong. Please try again later.';
          setErrorMessage(error);
        })
        .finally(() => {
          setIsDeleting(false);
        });
    }
  };
  return (
    <Dialog
      open={modalOpen}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
    >
      <DialogContent
        aria-labelledby='confirmation-delete-intent-label'
        className='flex max-w-xl flex-col items-center gap-0 rounded-2xl p-8 [&>button]:hidden'
      >
        <TriangleAlert
          aria-hidden
          className='h-12 w-12 fill-red-600 stroke-white'
        />
        <DialogTitle
          id='confirmation-delete-intent-label'
          className='my-4 text-[2rem] font-bold text-foreground'
        >
          Are you sure?
        </DialogTitle>
        <DialogDescription className='my-2 max-w-[30rem] break-words text-center text-[1.2rem] text-foreground'>
          You are about to delete a question.
        </DialogDescription>
        <p className='my-2 text-[1.2rem] font-bold'>
          This action is irreversible.
        </p>
        <div className='flex items-center justify-center p-2'>
          <Button
            variant='outline'
            className='m-2 w-36 px-4'
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant='destructive'
            loading={isDeleting}
            className='m-2 w-36 px-4'
            onClick={deleteIntent}
          >
            Delete
          </Button>
        </div>
        <div
          data-testid='delete-intent-error-slot'
          className={cn(
            'grid w-full self-stretch transition-all duration-300',
            errorMessage ? 'mt-2 grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className='overflow-hidden'>
            <ErrorAlert message={errorMessage} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteIntentModal;
